import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { 
  LayoutDashboard, Calendar, ClipboardList, Users, Scissors, 
  UserCog, Package, Receipt, BarChart3, Settings, Menu, Heart,
  Upload, GitMerge, Bot, LogOut, Mail, ChevronDown, ChevronRight,
  PanelLeftClose, PanelLeft, Gift, Headset, Building2, Star, Target, Trophy, QrCode
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";

const buildNavSections = ({ reviewsEnabled, businessMode }) => [
  {
    key: 'overview', label: 'Overview', defaultOpen: true,
    items: [
      { path: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
    ]
  },
  {
    key: 'operations', label: 'Operations', defaultOpen: true,
    items: [
      { path: "/admin/calendar", label: "Calendar", icon: Calendar },
      { path: "/admin/appointments", label: "Appointments", icon: ClipboardList },
      { path: "/admin/clients", label: "Clients", icon: Users },
      { path: "/admin/services", label: "Services", icon: Scissors },
      { path: "/admin/staff", label: "Staff", icon: UserCog },
      { path: "/admin/vouchers", label: "Vouchers", icon: Gift },
      { path: "/admin/competitions", label: "Competitions", icon: Trophy },
      ...(reviewsEnabled ? [{ path: "/admin/reviews", label: "Verified Reviews", icon: Star }] : []),
    ]
  },
  {
    key: 'automation', label: 'Automation', defaultOpen: false,
    items: [
      { path: "/admin/ai-assistant", label: "Beyond AI", icon: Bot },
      { path: "/admin/email-automations", label: "Email Automations", icon: Mail },
    ]
  },
  {
    key: 'finance', label: 'Inventory & Finance', defaultOpen: false,
    items: [
      { path: "/admin/inventory", label: "Inventory", icon: Package },
      { path: "/admin/expenses", label: "Expenses", icon: Receipt },
      { path: "/admin/targets", label: businessMode === 'team' ? "Team Targets" : "Monthly Target", icon: Target },
      { path: "/admin/reports", label: "Reports", icon: BarChart3 },
    ]
  },
  {
    key: 'system', label: 'System', defaultOpen: false,
    items: [
      { path: "/admin/sharing-tools", label: "Sharing Tools", icon: QrCode },
      { path: "/admin/import", label: "Import Data", icon: Upload },
      { path: "/admin/duplicates", label: "Merge Duplicates", icon: GitMerge },
      { path: "/admin/helpdesk", label: "Help Desk · AI", icon: Headset },
      { path: "/admin/settings", label: "Settings", icon: Settings },
    ]
  },
  {
    key: 'super_admin', label: 'Super-admin', defaultOpen: true, primaryOnly: true,
    items: [
      { path: "/admin/super-admin/salons", label: "Salons", icon: Building2 },
    ]
  }
];

const SidebarContent = ({ onClose, onLogout, compact = false, isPrimary = false, navSections, tenantSettings = {} }) => {
  const location = useLocation();
  const visibleSections = navSections.filter(s => !s.primaryOnly || isPrimary);
  const salonName = tenantSettings.salon_name || 'B∞kd';
  const salonLogo = tenantSettings.icon_logo_url || tenantSettings.logo_url || '';
  const [openSections, setOpenSections] = useState(() => {
    const saved = localStorage.getItem('salonos_sidebar_sections');
    if (saved) return JSON.parse(saved);
    return navSections.reduce((acc, s) => { acc[s.key] = s.defaultOpen; return acc; }, {});
  });

  useEffect(() => {
    localStorage.setItem('salonos_sidebar_sections', JSON.stringify(openSections));
  }, [openSections]);

  const toggleSection = (key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path) && item.path !== '/admin';
  };

  // Auto-expand section containing active item
  useEffect(() => {
    for (const section of visibleSections) {
      if (section.items.some(item => isActive(item))) {
        setOpenSections(prev => ({ ...prev, [section.key]: true }));
        break;
      }
    }
  }, [location.pathname]);

  return (
    <TooltipProvider delayDuration={compact ? 200 : 9999}>
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`border-b border-border ${compact ? 'p-2 py-3' : 'p-3'}`}>
        <Link to="/admin" className="flex items-center gap-2" onClick={onClose}>
          {salonLogo ? (
            <img
              src={salonLogo}
              alt={salonName}
              className={`rounded-xl object-cover ${compact ? 'w-8 h-8 mx-auto' : 'w-9 h-9'}`}
            />
          ) : (
            <div className={`rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold ${compact ? 'w-8 h-8 mx-auto' : 'w-9 h-9'}`}>
              {(salonName[0] || 'B').toUpperCase()}
            </div>
          )}
          {!compact && (
            <div>
              <h1 className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }} data-testid="sidebar-salon-name">{salonName}</h1>
              <p className="text-[9px] text-muted-foreground">B∞kd</p>
            </div>
          )}
        </Link>
      </div>

      {/* Nav */}
      <ScrollArea className="flex-1 py-1">
        <nav className={compact ? 'px-1' : 'px-2'}>
          {visibleSections.map((section) => {
            const isOpen = openSections[section.key];
            const hasActiveItem = section.items.some(item => isActive(item));

            return (
              <div key={section.key} className="mb-0.5">
                {/* Section header */}
                {!compact && section.items.length > 1 && (
                  <button 
                    onClick={() => toggleSection(section.key)}
                    className={`w-full flex items-center justify-between px-2 py-1.5 text-[9px] uppercase tracking-wider font-semibold text-muted-foreground/70 hover:text-muted-foreground transition-colors ${hasActiveItem ? 'text-muted-foreground' : ''}`}
                  >
                    {section.label}
                    {isOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                  </button>
                )}

                {/* Items */}
                {(compact || isOpen || section.items.length === 1) && (
                  <div className={`space-y-0.5 ${!compact && section.items.length > 1 ? '' : ''}`} style={{ transition: 'all 150ms ease' }}>
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item);
                      
                      const linkEl = (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={onClose}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                          className={`flex items-center gap-2.5 rounded-xl text-xs font-medium transition-colors duration-150 ${
                            compact ? 'p-2 justify-center' : 'px-2.5 py-2'
                          } ${
                            active 
                              ? 'bg-primary text-primary-foreground' 
                              : 'text-muted-foreground hover:bg-secondary hover:text-secondary-foreground'
                          }`}
                        >
                          <Icon size={compact ? 18 : 16} />
                          {!compact && item.label}
                        </Link>
                      );

                      if (compact) {
                        return (
                          <Tooltip key={item.path}>
                            <TooltipTrigger asChild>{linkEl}</TooltipTrigger>
                            <TooltipContent side="right" className="text-xs">{item.label}</TooltipContent>
                          </Tooltip>
                        );
                      }
                      return linkEl;
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className={`border-t border-border ${compact ? 'p-1 py-2' : 'p-2 py-3'} space-y-1`}>
        {compact ? (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to="/" className="flex items-center justify-center p-2 rounded-xl text-muted-foreground hover:text-primary transition-colors">
                  <Heart size={16} />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs">Booking page</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={onLogout} className="flex items-center justify-center p-2 rounded-xl text-muted-foreground hover:text-destructive transition-colors w-full">
                  <LogOut size={16} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs">Sign out</TooltipContent>
            </Tooltip>
          </>
        ) : (
          <>
            <Link to="/" className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-xs text-muted-foreground hover:text-primary transition-colors">
              <Heart size={13} /><span>Booking page</span>
            </Link>
            <button onClick={onLogout} className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-xs text-muted-foreground hover:text-destructive transition-colors w-full">
              <LogOut size={13} /><span>Sign out</span>
            </button>
          </>
        )}
      </div>
    </div>
    </TooltipProvider>
  );
};

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [compact, setCompact] = useState(() => localStorage.getItem('salonos_sidebar_compact') === 'true');
  const [tenantSettings, setTenantSettings] = useState({});
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const isPrimary = !!user?.isPrimary;

  // Load tenant-level config to render sidebar branding + decide conditional nav items.
  // Don't use lite=true — sidebar needs logo_url / icon_logo_url which `lite` strips.
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/settings`).then(res => {
      if (mounted) setTenantSettings(res.data || {});
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  const reviewMode = tenantSettings.review_mode || 'google';
  const reviewsEnabled = reviewMode === 'direct' || reviewMode === 'both';
  const businessMode = tenantSettings.business_mode || 'single';
  const navSections = buildNavSections({ reviewsEnabled, businessMode });

  const handleLogout = () => { logout(); navigate('/'); };

  const toggleCompact = () => {
    setCompact(prev => {
      localStorage.setItem('salonos_sidebar_compact', String(!prev));
      return !prev;
    });
  };

  return (
    <div className="admin-layout">
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex flex-col border-r border-border bg-white transition-all duration-200 ${compact ? 'w-[56px]' : 'w-[220px]'}`} style={{ flexShrink: 0 }}>
        {/* Collapse / expand toggle — pinned at top */}
        <button
          onClick={toggleCompact}
          title={compact ? 'Expand sidebar' : 'Collapse sidebar'}
          className={`border-b border-border text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors flex items-center gap-1.5 text-[10px] shrink-0 ${compact ? 'p-2 justify-center' : 'px-3 py-2 justify-end'}`}
        >
          {compact ? <PanelLeft size={14} /> : <><span>Collapse</span><PanelLeftClose size={14} /></>}
        </button>
        <div className="flex-1 overflow-hidden">
          <SidebarContent onClose={() => {}} onLogout={handleLogout} compact={compact} isPrimary={isPrimary} navSections={navSections} tenantSettings={tenantSettings} />
        </div>
      </aside>

      {/* Mobile header */}
      <div className="admin-content flex-1 overflow-x-hidden">
        <header className="md:hidden flex items-center justify-between p-3 border-b border-border bg-card">
          <Link to="/admin" className="flex items-center gap-2">
            {(tenantSettings.icon_logo_url || tenantSettings.logo_url) ? (
              <img src={tenantSettings.icon_logo_url || tenantSettings.logo_url} alt={tenantSettings.salon_name || 'Salon'} className="w-8 h-8 rounded-lg object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                {((tenantSettings.salon_name || 'B')[0]).toUpperCase()}
              </div>
            )}
            <span className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display' }} data-testid="mobile-header-salon-name">{tenantSettings.salon_name || 'B∞kd'}</span>
          </Link>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu size={20} /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[240px] p-0">
              <SidebarContent onClose={() => setMobileOpen(false)} onLogout={handleLogout} isPrimary={isPrimary} navSections={navSections} tenantSettings={tenantSettings} />
            </SheetContent>
          </Sheet>
        </header>

        <main className="p-4 sm:p-6 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
